import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { mediaUrl } from '../../config/api';
import { fieldKey } from '../../fieldKeys';
import { pathLabel } from '../../locations';
import FieldValue from '../FieldValue';
import { IconButton, Skeleton } from '../../ui';
import { CheckIcon, ChevronDownIcon, EditIcon, TrashIcon } from '../../ui/icons';
import { itemSummary, metaLine } from './itemSummary';
import { starNumber } from '../item/personal';
import { GroupTitle, groupSegments } from './groups';

const cx = (...names) => names.filter(Boolean).join(' ');

/**
 * An item's cover: the thumbnail (160 px) or, on dense screens and wide
 * cards, the 640 px one; lazy. Without a cover — or when it fails to load —
 * a placeholder with the collection's icon (and the title on large covers).
 */
export function ItemCover({ mediaId, title, icon: Icon, size = 'card' }) {
  const [failed, setFailed] = useState(false);
  if (mediaId && !failed) {
    return (
      <span className={`item-cover item-cover--${size}`}>
        <img
          src={mediaUrl(mediaId, 'thumb')}
          srcSet={size === 'card' ? `${mediaUrl(mediaId, 'thumb')} 160w, ${mediaUrl(mediaId, 'medium')} 640w` : undefined}
          sizes={size === 'card' ? '(max-width: 767px) 33vw, 200px' : undefined}
          alt=""
          data-testid="item-cover"
          loading="lazy"
          decoding="async"
          onError={() => setFailed(true)}
        />
      </span>
    );
  }
  return (
    <span className={`item-cover item-cover--${size} item-cover--empty`} aria-hidden="true">
      {Icon && <Icon size={size === 'card' ? 28 : 18} strokeWidth={1.6} />}
      {size === 'card' && title && <span className="item-cover__title">{title}</span>}
    </span>
  );
}

/** The item's link: its page, for everyone who can read the collection. */
function ItemLink({ to, className, children, onOpen }) {
  return (
    <Link to={to} state={{ fromCollection: true }} className={className} onClick={onOpen}>
      {children}
    </Link>
  );
}

/**
 * "★ 4 me · 4,4 avg" and a small tick when seen (Main.dc.html): what I
 * think of the item and, in a shared collection, the members' average.
 * Nothing when nothing is set.
 */
export function OpinionBadge({ item, personal, shared }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage || i18n.language || 'en';
  if (!personal) return null;
  const mine = item.mine || {};
  const ratings = item.ratings || {};
  const myRating = personal.rating && mine.rating != null ? starNumber(mine.rating, lang) : null;
  const average = personal.rating && shared && ratings.count > 0 ? starNumber(ratings.average, lang) : null;
  const done = personal.verbs.length > 0 && mine.done;
  if (myRating === null && average === null && !done) return null;
  const said = [
    myRating !== null && t('item.badge.mineSaid', { value: myRating }),
    average !== null && t('item.badge.averageSaid', { value: average, count: ratings.count }),
    done && t(`item.badge.done.${personal.verbs.length === 1 ? personal.verbs[0] : 'mixed'}`),
  ].filter(Boolean).join(', ');
  return (
    <span className="opinion-badge">
      <span aria-hidden="true" className="opinion-badge__text">
        {(myRating !== null || average !== null) && <span className="opinion-badge__star">★</span>}
        {[myRating !== null && t('item.badge.mine', { value: myRating }), average !== null && t('item.badge.average', { value: average })]
          .filter(Boolean).join(' · ')}
        {done && <CheckIcon size={14} strokeWidth={2.4} className="opinion-badge__done" />}
      </span>
      <span className="visually-hidden">{said}</span>
    </span>
  );
}

/**
 * A grouped list (phase 3 §4.3): one section per group along the page, its
 * heading spanning the row ("Salon 34"), the cards or rows under it. Not
 * grouped: the list alone.
 */
function Grouped({ items, group, groupInfo, children }) {
  if (!group) return children(items);
  return (
    <div className="item-groups">
      {groupSegments(items, group).map((segment, index) => (
        <section key={segment.key} className="item-group" aria-labelledby={`item-group-${index}`}>
          <h2 id={`item-group-${index}`} className="item-group__heading">
            <GroupTitle info={groupInfo} segment={segment} />
          </h2>
          {children(segment.items)}
        </section>
      ))}
    </div>
  );
}

/** Covers in a grid (the canvas's Main.dc.html): cover, title, year · creators, my opinion. */
export function ItemGrid({ items, fieldsOf, icon, itemPath, onOpen, highlightId, personal, shared, group, groupInfo }) {
  const { t } = useTranslation();
  return (
    <Grouped items={items} group={group} groupInfo={groupInfo}>
      {(shown) => (
        <ul className="item-grid">
          {shown.map((item) => {
            const summary = itemSummary(item, fieldsOf(item));
            const title = summary.title || t('collection.untitled');
            const meta = metaLine(summary, t('collection.andOthers'));
            return (
              <li key={item.id} className={cx('item-card', highlightId === item.id && 'is-new')}>
                <ItemLink to={itemPath(item)} onOpen={onOpen} className="item-card__link">
                  <ItemCover mediaId={item.cover_media_id} title={summary.title} icon={icon} />
                  <span className="item-card__title">{title}</span>
                  {meta && <span className="item-card__meta">{meta}</span>}
                  <OpinionBadge item={item} personal={personal} shared={shared} />
                </ItemLink>
              </li>
            );
          })}
        </ul>
      )}
    </Grouped>
  );
}

/** One line per item: small cover, title, subtitle, year · creators, type, my opinion, place. */
export function ItemList({
  items, fieldsOf, typeName, icon, itemPath, onOpen, highlightId, showLocation, showCovers, personal, shared, group, groupInfo,
}) {
  const { t } = useTranslation();
  return (
    <Grouped items={items} group={group} groupInfo={groupInfo}>
      {(shown) => (
        <ul className="item-list">
          {shown.map((item) => {
            const summary = itemSummary(item, fieldsOf(item));
            const meta = metaLine(summary, t('collection.andOthers'));
            const path = item.location_path && item.location_path.length > 0 ? pathLabel(t, item.location_path) : '';
            const type = typeName(item);
            return (
              <li key={item.id} className={cx('item-row', highlightId === item.id && 'is-new')}>
                <ItemLink to={itemPath(item)} onOpen={onOpen} className="item-row__link">
                  {showCovers && <ItemCover mediaId={item.cover_media_id} icon={icon} size="row" />}
                  <span className="item-row__text">
                    <span className="item-row__title">{summary.title || t('collection.untitled')}</span>
                    {summary.subtitle && <span className="item-row__subtitle">{summary.subtitle}</span>}
                    {(meta || type) && (
                      <span className="item-row__meta">
                        {[meta, type].filter(Boolean).join(' · ')}
                      </span>
                    )}
                    <OpinionBadge item={item} personal={personal} shared={shared} />
                  </span>
                  {showLocation && path && (
                    <span className="item-row__place location-path" title={path}><bdi>{path}</bdi></span>
                  )}
                </ItemLink>
              </li>
            );
          })}
        </ul>
      )}
    </Grouped>
  );
}

/**
 * The table of every visible field: sortable headers, a cover column when
 * the types have photos, the place (the end of its path stays visible), my
 * rating and the members' average when the types are rated; the title opens
 * the item's page for everyone, editors keep edit and delete.
 */
export function ItemTable({
  items, columns, sort, onSort, showCovers, showLocation, canEdit, itemPath, editPath, onOpen, onDelete, highlightId, icon,
  personal, shared, group, groupInfo,
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage || i18n.language || 'en';
  const rated = !!(personal && personal.rating);
  // The item's link sits in its title column (the field playing the title role), else the first.
  // Never a link or an image column: their values are links themselves.
  const linkable = (field) => !['link', 'image'].includes(field.type);
  const titleColumn = columns.findIndex((field) => field.role === 'title' && linkable(field));
  const linkColumn = titleColumn >= 0 ? titleColumn : columns.findIndex(linkable);
  const columnCount = (showCovers ? 1 : 0) + columns.length + (showLocation ? 1 : 0) + (rated ? 1 : 0) + (rated && shared ? 1 : 0) + (canEdit ? 1 : 0);
  const header = (key, label) => {
    const active = sort.key === key;
    return (
      <th key={key} scope="col" aria-sort={active ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none'}>
        <button type="button" className={cx('item-table__sort', active && 'is-active')} onClick={() => onSort(key)}
                title={t('collection.sortBy')}>
          <span>{label}</span>
          <ChevronDownIcon size={14} strokeWidth={2.2}
                           className={cx('item-table__arrow', active && sort.direction === 'asc' && 'is-up', !active && 'is-idle')} />
        </button>
      </th>
    );
  };
  return (
    <div className="item-table-wrap">
      <table className="item-table">
        <thead>
          <tr>
            {showCovers && <th scope="col" className="item-table__cover"><span className="visually-hidden">{t('collection.cover')}</span></th>}
            {columns.map((field) => header(fieldKey(field), field.name))}
            {showLocation && header('location', t('collection.location'))}
            {rated && header('@mine', t('item.columns.mine'))}
            {rated && shared && header('@average', t('item.columns.average'))}
            {canEdit && <th scope="col" className="item-table__actions"><span className="visually-hidden">{t('collection.actions')}</span></th>}
          </tr>
        </thead>
        {groupSegments(items, group).map((segment) => (
          <tbody key={segment.key}>
            {group && (
              // A grouped table: each group is its own row group, under a header spanning every column.
              <tr className="item-table__group">
                <th scope="rowgroup" colSpan={columnCount}>
                  <GroupTitle info={groupInfo} segment={segment} />
                </th>
              </tr>
            )}
            {segment.items.map((item) => {
              const path = item.location_path && item.location_path.length > 0 ? pathLabel(t, item.location_path) : '';
              return (
                <tr key={item.id} className={highlightId === item.id ? 'just-added' : undefined}>
                  {showCovers && (
                    <td className="item-table__cover"><ItemCover mediaId={item.cover_media_id} icon={icon} size="row" /></td>
                  )}
                  {columns.map((field, index) => {
                    const value = <FieldValue field={field} value={item.field_values && item.field_values[fieldKey(field)]} />;
                    return (
                      <td key={fieldKey(field)}>
                        {index === linkColumn
                          ? <Link to={itemPath(item)} state={{ fromCollection: true }} onClick={onOpen} className="item-table__open">{value}</Link>
                          : value}
                      </td>
                    );
                  })}
                  {showLocation && (
                    <td>{path ? <span className="location-path" title={path}><bdi>{path}</bdi></span> : <span className="item-table__none">-</span>}</td>
                  )}
                  {rated && (
                    <td className="item-table__number">
                      {item.mine && item.mine.rating != null ? <>★&nbsp;{starNumber(item.mine.rating, lang)}</> : <span className="item-table__none">-</span>}
                    </td>
                  )}
                  {rated && shared && (
                    <td className="item-table__number">
                      {item.ratings && item.ratings.count > 0
                        ? <span title={t('item.opinion.averageOf', { count: item.ratings.count })}>{starNumber(item.ratings.average, lang)}</span>
                        : <span className="item-table__none">-</span>}
                    </td>
                  )}
                  {canEdit && (
                    <td className="item-table__actions">
                      <span className="item-table__buttons">
                        <IconButton as={Link} to={editPath(item)} state={{ fromCollection: true }} onClick={onOpen}
                                    icon={EditIcon} variant="ghost" size="sm" label={t('common.edit')} />
                        <IconButton icon={TrashIcon} variant="ghost" size="sm" className="item-table__delete"
                                    label={t('collection.deleteItem')} onClick={() => onDelete(item)} />
                      </span>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        ))}
      </table>
    </div>
  );
}

/** Placeholders shaped like the view while the first page loads. */
export function ResultsSkeleton({ view }) {
  if (view === 'grid') {
    return (
      <ul className="item-grid" aria-hidden="true">
        {Array.from({ length: 12 }, (_, i) => (
          <li key={i} className="item-card">
            <Skeleton className="item-cover item-cover--card" height="auto" radius="var(--radius-sm)" />
            <Skeleton height={14} width="80%" />
            <Skeleton height={12} width="50%" />
          </li>
        ))}
      </ul>
    );
  }
  return (
    <ul className={view === 'list' ? 'item-list' : 'item-list item-list--table'} aria-hidden="true">
      {Array.from({ length: 8 }, (_, i) => (
        <li key={i} className="item-row item-row--skeleton">
          <Skeleton width={view === 'list' ? 40 : 32} height={view === 'list' ? 60 : 48} radius="var(--radius-xs)" />
          <span className="item-row__text">
            <Skeleton height={14} width={`${40 + ((i * 17) % 40)}%`} />
            <Skeleton height={12} width="30%" />
          </span>
        </li>
      ))}
    </ul>
  );
}
